import React from 'react'
import Head from 'next/head'
import Image from 'next/image'
import { PAGE_TITLE_PREFIX } from '~/components/constants'
import Layout from '~/components/Layout'
import { Column, Grid } from '~/components/Grid'
import { GetStaticProps, InferGetStaticPropsType } from 'next'
import { readToken } from '~/lib/sanity.api'
import { getClient } from '~/lib/sanity.client'
import { getNavigation, Navigation } from '~/lib/sanity.queries'
import type { SharedPageProps } from '~/pages/_app'
import { getBrandPage, type BrandPage } from '~/lib/sanity.queries'
import { urlFor } from '~/utils'
import { downloadAllFiles } from '~/utils/download'
import { motion } from 'framer-motion'
import Card from '~/components/Card'

export const getStaticProps: GetStaticProps<
  SharedPageProps & {
    navigation: Navigation
    brandPage: BrandPage
  }
> = async ({ draftMode = false }) => {
  const client = getClient(draftMode ? { token: readToken } : undefined)
  const navigation = await getNavigation(client)
  const brandPage = await getBrandPage(client)

  return {
    props: {
      draftMode,
      token: draftMode ? readToken : '',
      navigation,
      brandPage,
    },
  }
}

const MediaKit = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const pageTile = `${PAGE_TITLE_PREFIX} Media Kit`
  const { brandPage } = props

  return (
    <>
      <Head>
        <title>{pageTile}</title>
      </Head>
      <Layout navigation={props.navigation}>
        <section className="pt-header md:pt-header-lg">
          <Grid>
            <Column className="col-span-12 text-center my-24">
              <motion.h1 
                className="h1 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {brandPage.pageTitle}
              </motion.h1>
              <motion.p 
                className="body-large text-grey max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {brandPage.pageDescription}
              </motion.p>
            </Column>
          </Grid>

          {/* Logos Section */}
          <Grid className="mb-32">
            <Column className="col-span-12 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="h3 mb-4">{brandPage.logos.sectionTitle}</h2>
                <p className="body-small text-grey">
                  {brandPage.logos.description}
                </p>
              </motion.div>
            </Column>

            {brandPage.logos.logoVariants.map((variant, index) => (
              <Column key={variant.title} className="col-span-12 md:col-span-4">
                <motion.div 
                  className="bg-grey-150 rounded-card p-8 flex flex-col gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className={`aspect-square relative ${index === 0 ? 'bg-white' : index === 1 ? 'bg-green' : 'bg-black'} rounded-lg flex items-center justify-center`}>
                    <Image
                      src={urlFor(variant.image).url()}
                      alt={variant.title}
                      width={200}
                      height={200}
                      className="w-1/2 h-1/2 object-contain"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="mono-small">{variant.title}</span>
                    <button className="text-green hover:text-lime transition-colors mono-small"
                      onClick={() => downloadAllFiles(variant.downloadFiles)}
                    >
                      Download all
                    </button>
                  </div>
                </motion.div>
              </Column>
            ))}
          </Grid>

          {/* Typography Section */}
          <Grid className="mb-32">
            <Column className="col-span-12 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="h3 mb-4">Typography</h2>
                <p className="body-small text-grey">
                  Our brand fonts and typography guidelines
                </p>
              </motion.div>
            </Column>

            <Column className="col-span-12 md:col-span-6">
              <Card>
                <div className="flex flex-col h-full w-full">
                  <div className="flex items-start justify-between">
                    <div className="space-y-6 flex-1">
                      <div className="relative w-12 h-12">
                        <div className="absolute inset-0 bg-green/20 rounded-lg transform rotate-45" />
                        <span className="text-green absolute inset-0 m-auto font-sans text-xl flex items-center justify-center">Aa</span>
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-medium text-white mb-4">Inter</h3>
                        <p className="text-grey text-base leading-relaxed">Primary font for all body text and headlines</p>
                      </div>

                      <div className="rounded-lg px-4 py-3 text-sm text-grey">
                        <div className="flex flex-col gap-4">
                          <div className="font-sans">
                            <p className="text-4xl">Aa</p>
                            <p className="mono-small text-grey mt-2">Bold - 700</p>
                          </div>
                          <div className="font-sans font-normal">
                            <p className="text-2xl">Aa</p>
                            <p className="mono-small text-grey mt-2">Regular - 400</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </Column>

            <Column className="col-span-12 md:col-span-6">
              <Card>
                <div className="flex flex-col h-full w-full">
                  <div className="flex items-start justify-between">
                    <div className="space-y-6 flex-1">
                      <div className="relative w-12 h-12">
                        <div className="absolute inset-0 bg-green/20 rounded-lg transform rotate-45" />
                        <span className="text-green absolute inset-0 m-auto font-mono text-xl flex items-center justify-center">Aa</span>
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-medium text-white mb-4">Space Mono</h3>
                        <p className="text-grey text-base leading-relaxed">Secondary font for labels and accents</p>
                      </div>

                      <div className="rounded-lg px-4 py-3 text-sm text-grey">
                        <div className="flex flex-col gap-4">
                          <div className="font-mono">
                            <p className="text-4xl">Aa</p>
                            <p className="mono-small text-grey mt-2">Bold - 700</p>
                          </div>
                          <div className="font-mono font-normal">
                            <p className="text-2xl">Aa</p>
                            <p className="mono-small text-grey mt-2">Regular - 400</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </Column>
          </Grid>

          {/* Colors Section */}
          <Grid className="mb-32">
            <Column className="col-span-12 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="h3 mb-4">Colors</h2>
                <p className="body-small text-grey">
                  These colors ensure our brand&apos;s visual identity is modern,
                  approachable, and aligned with our innovative technology.
                </p>
              </motion.div>
            </Column>

            {brandPage.colorPalette?.map((color, index) => (
              <Column key={color.hexCode} className="col-span-6 md:col-span-3">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div 
                    className="aspect-square rounded-lg mb-4" 
                    style={{ backgroundColor: color.hexCode }}
                  ></div>
                  <p className="mono-small">{color.hexCode}</p>
                  <p className="mono-small text-grey">{color.colorName}</p>
                </motion.div>
              </Column>
            ))}
          </Grid>
        </section>
      </Layout>
    </>
  )
}

export default MediaKit
